"use client";

import { c as _c } from "react/compiler-runtime";
import { getFileIcon } from "@/lib/file-utils";
import { cx } from "@/lib/tailwind-utils";
import type { DocumentSummary } from "@/types/document";
import { withPdfPreviewParams } from "./document-thumbnail";

// Browser-renderable image types. TIFF is excluded — it contains "image" but
// browsers can't render it in an <img>, so it falls back to click-to-view.
const INLINE_IMAGE_RE = /image\/(jpe?g|png|gif|webp)/;
interface IdDocumentPreviewProps {
  document: DocumentSummary;
  signedUrl: string | undefined;
  /** Whether the signed-URL request is still pending (shows a skeleton). */
  isLoading?: boolean;
  /** Height of the preview area, e.g. "h-72" (overview) or "h-40" (sidebar). */
  heightClassName?: string;
}

/**
 * Shared inline preview for an ID document, used by the Overview card and the
 * sidebar section. Images render with object-contain so the whole license is
 * readable; multi-page PDFs render their first page via an iframe. Types the
 * browser can't display inline (e.g. multi-page TIFF) fall back to a
 * click-to-view prompt.
 */
export default function IdDocumentPreview(t0) {
  const $ = _c(26);
  const {
    document,
    signedUrl,
    isLoading: t1,
    heightClassName: t2
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const heightClassName = t2 === undefined ? "h-72" : t2;
  const type = document.fileType?.toLowerCase() ?? "";
  const isImage = INLINE_IMAGE_RE.test(type);
  const isPdf = type.includes("pdf");
  if (isImage && signedUrl) {
    const t3 = document.title;
    let t4;
    if ($[0] !== heightClassName) {
      t4 = cx("w-full object-contain", heightClassName);
      $[0] = heightClassName;
      $[1] = t4;
    } else {
      t4 = $[1];
    }
    let t5;
    if ($[2] !== document.title || $[3] !== signedUrl || $[4] !== t4) {
      t5 = <img src={signedUrl} alt={t3} className={t4} />;
      $[2] = document.title;
      $[3] = signedUrl;
      $[4] = t4;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    return t5;
  }
  if (isPdf && signedUrl) {
    let t3;
    if ($[6] !== signedUrl) {
      t3 = withPdfPreviewParams(signedUrl);
      $[6] = signedUrl;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    const t4 = `${document.title} preview`;
    let t5;
    if ($[8] !== heightClassName) {
      t5 = cx("pointer-events-none w-full bg-background", heightClassName);
      $[8] = heightClassName;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    let t6;
    if ($[10] !== t3 || $[11] !== t4 || $[12] !== t5) {
      t6 = <iframe src={t3} title={t4} className={t5} tabIndex={-1} />;
      $[10] = t3;
      $[11] = t4;
      $[12] = t5;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    return t6;
  }
  if ((isImage || isPdf) && isLoading) {
    let t3;
    if ($[14] !== heightClassName) {
      t3 = cx("w-full animate-pulse bg-muted", heightClassName);
      $[14] = heightClassName;
      $[15] = t3;
    } else {
      t3 = $[15];
    }
    let t4;
    if ($[16] !== t3) {
      t4 = <div className={t3} />;
      $[16] = t3;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    return t4;
  }
  let t3;
  if ($[18] !== heightClassName) {
    t3 = cx("flex flex-col items-center justify-center gap-2 text-foreground-secondary", heightClassName);
    $[18] = heightClassName;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  let t4;
  if ($[20] !== document.fileType) {
    t4 = getFileIcon(document.fileType);
    $[20] = document.fileType;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  let t5;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="text-caption">Click to view ID document</span>;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[23] !== t3 || $[24] !== t4) {
    t6 = <div className={t3}>{t4}{t5}</div>;
    $[23] = t3;
    $[24] = t4;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  return t6;
}